/**
 * הרשאות תמיכה — the only bridge from the platform into a customer's data. 04 §13.1.5, §6.4, OA-0002.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHY A PLACE OF ITS OWN, AND NOT A PANEL ON A CUSTOMER'S CARD
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * A grant has a lifecycle that outlives the screen it was requested from. It is REQUESTED by one
 * principal and APPROVED BY A DIFFERENT ONE — that is the dual control, and it is what stops a
 * platform with a single operator from opening a path into a customer's data on its own. The person
 * doing the approving is looking for *"what is waiting on me"*, not for a customer; a queue that
 * lived inside one customer's card would give them nowhere to stand and no way to find the request.
 *
 * The customer card links here, and the request form on this screen names its customer. Both
 * directions work, and neither is the only one.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * A FRESH GRANT IS BLOCKED BY TWO THINGS, AND THE SCREEN NAMES BOTH
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * A grant is created UNAPPROVED and, where the customer requires it, WITHOUT CONSENT. So the ordinary
 * state of a new request is "blocked by two", and a chip saying "ממתין" would hide which of them the
 * operator can actually act on — approval is theirs, consent is the customer's and never theirs to
 * supply. `blockedBy` arrives as a list for that reason, and every member of it is written out.
 *
 * ── WHAT IS DECLARED AND NEVER DEFAULTED ────────────────────────────────────────────────────────
 *
 * `isRelatedParty`, and it is a required choice with two visible answers rather than a checkbox —
 * OA-0002 makes it a falsifiable assertion attributable to a named requester, and a field that means
 * "no" when nobody touched it is a note. `requiresConsent` is the same shape for the same reason.
 *
 * ── AND THE CUSTOMER IS NEVER LOCKED OUT OF THEIR OWN DOOR ──────────────────────────────────────
 *
 * Revocation here is *"the second route to the same fact, never the only one"*: the customer may
 * always revoke from their own console, and every call made under a grant is audited ALWAYS and shows
 * up on their own security screen. The sentence at the top of this screen says so, on the platform
 * side, where the person who might forget it is standing.
 */

import { useCallback, useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { DataTable } from '../components/DataTable';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { Column } from '../components/DataTable';
import type { SupportGrantBlocker, SupportGrantMode, SupportGrantSummary } from '../api/contract';
import { SUPPORT_GRANT_MODES } from '../api/contract';
import { formatInstantHe } from '../api/adminVocabulary';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { ServerRead } from '../components/ServerRead';
import { listOf, useServerData } from '../api/useServerData';
import { routes } from '../routes';

const TITLE_HE = 'הרשאות תמיכה';

/**
 * What is still standing between a grant and being usable. Four states, four sentences — and the
 * first two are the pair that a single "pending" chip would merge.
 */
const BLOCKER_HE: Readonly<Record<SupportGrantBlocker, string>> = {
  awaiting_approval: 'ממתין לאישור של אדם שני בפלטפורמה',
  awaiting_consent: 'ממתין להסכמת הארגון — הסכמה שאיננו יכולים לתת במקומו',
  expired: 'חלון הזמן הסתיים',
  revoked: 'ההרשאה בוטלה',
};

const MODE_HE: Readonly<Record<SupportGrantMode, string>> = {
  READ_ONLY: 'צפייה בלבד',
  READ_WRITE: 'צפייה ועדכון',
};

type Outcome =
  | { readonly kind: 'requested' }
  | { readonly kind: 'approved' }
  | { readonly kind: 'revoked' }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

export function PlatformSupportGrantsScreen(): JSX.Element {
  const ctx = useWorkContext();

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  return <PlatformSupportGrantsBody />;
}

/**
 * THE BODY, AND WHY IT IS A SEPARATE COMPONENT.
 *
 * Every hook on this screen lives here, BELOW the surface guard, because `ctx.surface` is `null`
 * until `auth.whoami.read` answers (`shell/ContextProvider.tsx:181`) and every in-app navigation is
 * a full page load. Declared in one component with the guard above them, the early return would run
 * on the first (unresolved) render and the hooks on the second — React then sees two different hook
 * orders for one component and throws DURING RENDER, and the arrival is a blank page.
 *
 * It is a SPLIT and not a hoist. `useServerData` has no `enabled` option
 * (`api/useServerData.ts:73`), so lifting the reads above the guard would issue a PLATFORM read on
 * an unresolved or wrong-surface render — a denied request, and an audited write, for a screen the
 * caller is not on. Here nothing is read until the session has been stated as PLATFORM, and each
 * component's hook order is unconditional.
 */
function PlatformSupportGrantsBody(): JSX.Element {
  const [tenantUid, setTenantUid] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<SupportGrantMode>('READ_ONLY');
  const [requiresConsent, setRequiresConsent] = useState<'yes' | 'no' | null>(null);
  const [relatedParty, setRelatedParty] = useState<'yes' | 'no' | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  /*
   * READ FROM THE SERVER. **Phase 2 §6, vertical C.**
   *
   * A support grant is the ONLY bridge between GL-POINT's own staff and a customer's data, so this
   * list is the single screen on which every crossing is visible. Its three WRITES were already
   * wired — an operator could approve, extend and revoke — while the list they act on was
   * development data: approving a grant that was never fetched, against a `grantUid` from a local
   * constant.
   *
   * The tenants come from the same `platform.tenant.read` the tenants screen uses, because a grant
   * names a `tenantUid` and this screen shows the customer's name beside it. It DEGRADES: if that
   * read fails the uid stands in for the name, which is true and ugly rather than absent.
   */
  const readGrants = useCallback(() => callOperation('platform.support_grant.read', {}), []);
  const readTenants = useCallback(() => callOperation('platform.tenant.read', {}), []);
  const grantsRead = useServerData(readGrants);
  const tenantsRead = useServerData(readTenants);

  /*
   * THE SIGNED-IN IDENTITY, FOR ONE PURPOSE ONLY. **X6 (R-3), UX B4.**
   *
   * Used to suppress the approve control on rows this operator requested. It is an AFFORDANCE and
   * is written down as one: the refusal is the store's `requested_by_user_id != @approver` predicate
   * inside the conditional UPDATE (`packages/db/src/support-access-store.ts`, `approveSupportGrant`) and
   * `CK_support_grant_dual_control` beneath it, and both hold for a caller holding every capability
   * with no screen in the process. Hiding the button does not make self-approval impossible; it
   * makes it VISIBLY impossible, which is the separate thing a queue of one operator's own requests
   * needs. The standing notice above already says so in words, pre-announced, before any row exists.
   */
  const ctx = useWorkContext();
  const signedInUserUid = ctx.session?.userUid ?? null;

  const grants: readonly SupportGrantSummary[] = listOf(grantsRead.result, 'grants');
  const tenants = listOf(tenantsRead.result, 'tenants');
  const waiting = grants.filter((g) => g.blockedBy.includes('awaiting_approval')).length;

  async function run(kind: Exclude<Outcome, { kind: 'failed' }>['kind'], call: () => Promise<unknown>): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      await call();
      setOutcome({ kind } as Outcome);
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  function requestGrant(e: FormEvent): void {
    e.preventDefault();
    void run('requested', () =>
      callOperation('platform.support_grant.request', {
        params: { tenant_uid: tenantUid },
        body: {
          reason,
          ticketRef: null,
          mode,
          // Both DECLARED. Neither has a default: see the header.
          requiresConsent: requiresConsent === 'yes',
          capabilitySet: [],
          isRelatedParty: relatedParty === 'yes',
        },
      }),
    );
  }

  /**
   * IS THIS ROW THE VIEWER'S OWN REQUEST?
   *
   * `null` on either side is NOT a match. An unknown requester (the LEFT JOIN's missing row) and an
   * unresolved session are both "I cannot tell", and the honest answer to "I cannot tell" here is to
   * leave the control standing and let the store refuse — not to hide a control the operator may
   * legitimately need. Hiding on uncertainty would turn an affordance into a silent, wrong control.
   */
  const isOwnRequest = (g: SupportGrantSummary): boolean =>
    signedInUserUid !== null && g.requestedByUserUid === signedInUserUid;

  const columns: readonly Column<SupportGrantSummary>[] = [
    {
      key: 'tenant',
      headerHe: 'הלקוח',
      render: (g) => (
        <span className="company-cell">
          <span className="company-cell__name">{g.tenantLegalName}</span>
          {/*
            * THE REQUESTER, AND THE NULL IS **NAMED** RATHER THAN HIDDEN. **D2, AMEND-3 §C-4.**
            *
            * X9 omitted the segment when the producer's LEFT JOIN answered null, on the grounds that
            * no copy had been issued for it. It has been now, and the ruling says more than "here is a
            * string": that null is not a missing optional. `requested_by_user_id` is NOT NULL with an
            * enforced foreign key, `display_name` is NOT NULL, and nothing in this product deletes or
            * anonymises an `app_user` row — so there is NO legitimate state that produces it. A null
            * here means BROKEN REFERENTIAL INTEGRITY.
            *
            * Omitting it printed the customer's name with no requester beside it, which reads as
            * "nobody asked for this" on the ONE screen where an operator decides whether to let
            * support into a customer's data. A row whose requester cannot be established must look
            * wrong, not tidy.
            *
            * The label never interpolates the null (`ביקש/ה: null` was the original defect) and never
            * guesses a name.
            */}
          <span className="company-cell__id">
            {g.requestedByDisplayName === null
              ? 'ביקש/ה: לא נמצאה רשומת מבקש'
              : `ביקש/ה: ${g.requestedByDisplayName}`}
          </span>
        </span>
      ),
    },
    { key: 'reason', headerHe: 'הסיבה', render: (g) => g.reason },
    { key: 'mode', headerHe: 'היקף', render: (g) => MODE_HE[g.mode] },
    {
      key: 'window',
      headerHe: 'חלון הזמן',
      render: (g) => (
        <span dir="ltr">
          {formatInstantHe(g.startsAtUtc)} — {formatInstantHe(g.expiresAtUtc)}
        </span>
      ),
    },
    {
      key: 'state',
      headerHe: 'מה חוסם',
      /*
       * Every blocker, never the first. Approval is the operator's; consent is not.
       *
       * AND THE EMPTY BRANCH STATES ONLY WHAT THIS LIST KNOWS. It used to read
       * "בתוקף — התמיכה יכולה להיכנס" - in force, support can enter - which is gate stage 7's
       * conclusion restated by a list that reads none of stage 7's inputs: not `capability_set_json`,
       * not the session surface, not tenant status. An empty `blockedBy` means "nothing RECORDED
       * here is stopping it", never "support can get in". `רשומות` is the load-bearing word and it
       * binds the claim to the four recorded facts the list actually read.
       *
       * The tone is `muted` and not `ok` for the same reason: the phrase asserts no verdict, so the
       * chrome must not assert one either. `warn` is wrong in the other direction - a grant with
       * nothing recorded against it is the normal expected row, not a caution.
       */
      render: (g) =>
        g.blockedBy.length === 0 ? (
          <StatusChip labelHe="ללא חסימות רשומות" tone="muted" />
        ) : (
          <span className="chip-row">
            {g.blockedBy.map((blocker) => (
              <span key={blocker} className="chip">
                {BLOCKER_HE[blocker]}
              </span>
            ))}
          </span>
        ),
    },
    {
      key: 'related',
      headerHe: 'צד קשור',
      render: (g) =>
        g.isRelatedParty ? <StatusChip labelHe="הוצהר כצד קשור" tone="error" /> : 'לא',
    },
    {
      key: 'actions',
      headerHe: 'פעולות',
      render: (g) => (
        <span className="chip-row">
          {g.blockedBy.includes('awaiting_approval') && !isOwnRequest(g) ? (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={sending}
              onClick={() =>
                void run('approved', () =>
                  callOperation('platform.support_grant.approve', {
                    params: { grant_uid: g.grantUid },
                    body: {},
                  }),
                )
              }
            >
              אישור
            </button>
          ) : null}
          {g.blockedBy.includes('expired') || g.blockedBy.includes('revoked') ? null : (
            <button
              type="button"
              className="btn btn--quiet"
              disabled={sending || revokeReason === ''}
              onClick={() =>
                void run('revoked', () =>
                  callOperation('platform.support_grant.revoke', {
                    params: { grant_uid: g.grantUid },
                    body: { reason: revokeReason },
                  }),
                )
              }
            >
              ביטול
            </button>
          )}
        </span>
      ),
    },
  ];

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'קונסולת הפלטפורמה', href: routes.platformConsole }}
    >
      <section className="notice" data-tone="warn">
        <h2 className="notice__title">כניסה לנתוני לקוח היא פעולה חריגה</h2>
        <p className="notice__body">
          הרשאת תמיכה היא הדרך היחידה שבה מישהו מהפלטפורמה נכנס לנתונים של ארגון. היא מוגבלת בזמן,
          נדרשת בה סיבה כתובה, והיא אינה נכנסת לתוקף עד שאדם שני בפלטפורמה מאשר אותה. אישור עצמי אינו
          אפשרי — פלטפורמה עם מפעיל אחד אינה יכולה לפתוח לעצמה דלת.
        </p>
        <p className="notice__body">{`${grants.length} הרשאות ברשימה · ${waiting} מהן ממתינות לאישור`}</p>
      </section>

      {outcome === null ? null : outcome.kind === 'requested' ? (
        <StateBlock
          titleHe="הבקשה נרשמה"
          bodyHe="ההרשאה נוצרה ואינה בתוקף. היא תיכנס לתוקף רק לאחר אישור של אדם שני, ובארגון שדורש זאת — גם לאחר הסכמת הארגון."
        />
      ) : outcome.kind === 'approved' ? (
        <StateBlock titleHe="ההרשאה אושרה" bodyHe="האישור נרשם יחד עם מי שנתן אותו." />
      ) : outcome.kind === 'revoked' ? (
        <StateBlock
          titleHe="ההרשאה בוטלה"
          bodyHe="הכניסה נסגרה מיד. הרשומה נשמרת יחד עם הסיבה, ומה שנעשה תחת ההרשאה נשאר ביומן."
        />
      ) : (
        <ActionFailureNotice
          failure={outcome.failure}
          /*
           * THE FACTOR IS GIVEN HERE. **Owner section 3, 2026-10-27.**
           *
           * Without this handler `ActionFailureNotice` has no way to satisfy an MFA demand in
           * place and falls through to the arm that LINKS TO ANOTHER ADDRESS, which unmounts
           * this screen and loses whatever was half-entered. Every write this screen makes is
           * declared `per_action`, so the demand is a state it really reaches.
           *
           * NOTHING IS RE-SENT. `TenantUsersScreen` states the rule: *"A write that replays
           * itself after an authentication step is a write nobody pressed."* Clearing the
           * refusal puts the person back where they were, and they press again.
           */
          onStepUpSatisfied={() => setOutcome(null)}
        />
      )}

      <ServerRead result={grantsRead.result} whatHe="את הרשאות התמיכה" skeletonRows={6}>
        {(data) => (
          <DataTable<SupportGrantSummary>
            captionHe="הרשאות תמיכה"
            columns={columns}
            rows={data.grants}
            rowKey={(g) => g.grantUid}
            filterSummaryHe={`מציג ${data.grants.length} הרשאות תמיכה — כולל כאלה שהסתיימו`}
            // An empty list here is a STATEMENT: nobody from GL-POINT holds a way into a customer's
            // data right now. A refused read must never be shown as that sentence, which is what
            // the wrapper above is for.
            emptyStateHe="לא נרשמו הרשאות תמיכה."
          />
        )}
      </ServerRead>

      <label className="field">
        <span className="field__label">סיבת הביטול</span>
        <input
          className="field__input"
          name="revoke-reason"
          value={revokeReason}
          onChange={(e) => setRevokeReason(e.currentTarget.value)}
        />
      </label>

      <form className="form" onSubmit={requestGrant}>
        <h2 className="form__title">בקשת הרשאת תמיכה</h2>

        <fieldset className="field field--choice">
          <legend className="field__label">הארגון</legend>
          {tenants.map((tenant) => (
            <label key={tenant.tenantUid} className="choice">
              <input
                type="radio"
                name="grant-tenant"
                value={tenant.tenantUid}
                checked={tenantUid === tenant.tenantUid}
                onChange={() => setTenantUid(tenant.tenantUid)}
              />
              <span>{tenant.legalName}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="field field--choice">
          <legend className="field__label">היקף ההרשאה</legend>
          {SUPPORT_GRANT_MODES.map((candidate) => (
            <label key={candidate} className="choice">
              <input
                type="radio"
                name="grant-mode"
                value={candidate}
                checked={mode === candidate}
                onChange={() => setMode(candidate)}
              />
              <span>{MODE_HE[candidate]}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="field field--choice">
          <legend className="field__label">האם נדרשת הסכמת הארגון</legend>
          <label className="choice">
            <input
              type="radio"
              name="requiresConsent"
              value="yes"
              checked={requiresConsent === 'yes'}
              onChange={() => setRequiresConsent('yes')}
            />
            <span>נדרשת הסכמה</span>
          </label>
          <label className="choice">
            <input
              type="radio"
              name="requiresConsent"
              value="no"
              checked={requiresConsent === 'no'}
              onChange={() => setRequiresConsent('no')}
            />
            <span>אינה נדרשת</span>
          </label>
        </fieldset>

        <fieldset className="field field--choice">
          <legend className="field__label">האם מדובר בצד קשור</legend>
          <label className="choice">
            <input
              type="radio"
              name="grant-related"
              value="no"
              checked={relatedParty === 'no'}
              onChange={() => setRelatedParty('no')}
            />
            <span>אינו צד קשור</span>
          </label>
          <label className="choice">
            <input
              type="radio"
              name="grant-related"
              value="yes"
              checked={relatedParty === 'yes'}
              onChange={() => setRelatedParty('yes')}
            />
            <span>צד קשור</span>
          </label>
        </fieldset>

        <label className="field">
          <span className="field__label">הסיבה לכניסה</span>
          <input
            className="field__input"
            name="grant-reason"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <span className="field__hint">
            הסיבה נרשמת ומוצגת גם לארגון עצמו.
          </span>
        </label>

        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={
              sending ||
              tenantUid === '' ||
              reason === '' ||
              requiresConsent === null ||
              relatedParty === null
            }
          >
            בקשת הרשאה
          </button>
        </div>
        <p className="form__note">
          הבקשה נוצרת ללא אישור וללא הסכמה. עד שיינתנו, המערכת דוחה כל ניסיון להשתמש בה.
        </p>
      </form>
    </AppShell>
  );
}
